import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * IntroSplash — افتتاحية سينمائية واقعية بهوية AUFQ.
 *
 * مدينة عند فجر ذهبي، مبنية بتقنيات تصوير واقعية (لا مظهر رسومي):
 * - منظور جوي: ثلاث طبقات عمق، الأبعد أفتح وأكثر ضبابية (Gaussian blur).
 * - أبراج زجاجية بانعكاسات قطرية وأعمدة واجهات، ونوافذ فردية تُضاء
 *   عشوائياً بدرجات دافئة/باردة متفاوتة (توليد عشوائي ثابت البذرة).
 * - شمس تشرق خلف الأبراج مع توهّج حجمي وأشعة ضوئية وضباب عند الأفق.
 * - أرض رطبة تعكس الأبراج، غبار بناء ناعم عند ارتطام كل برج.
 * - طيور عابرة، سحب مضاءة من الأسفل، حبيبات فيلم + تظليل عدسة (vignette).
 * - حركة كاميرا بطيئة (pull-back) لإحساس لقطة حقيقية.
 *
 * يعمل عند كل تحميل كامل للصفحة، قابل للتخطي بنقرة،
 * ولا يُعرض إطلاقاً مع prefers-reduced-motion.
 */

const TOTAL_MS = 10500
const LEAVE_MS = 700

// الشعار ثلاثي الأبعاد يبدأ بعد اكتمال نهوض الأبراج القريبة (~٢.٩ث)
const LOGO_START_S = 2.9
// إحداثيات y للخطوط الذهبية الـ١٤ — من الأسفل (s1) إلى الأعلى (s14)
const STRIPE_YS = [122.2, 116.8, 111.4, 106, 100.6, 95.2, 89.8, 84.4, 79, 73.6, 68.2, 62.8, 57.4, 52]

// ───── إعداد المشهد (viewBox 1200×700، خط الأرض y=640) ─────
const GROUND = 640

// مولّد عشوائي ثابت البذرة — نتيجة متطابقة في كل عرض
function mulberry32(seed: number) {
  let t = seed
  return () => {
    t += 0x6d2b79f5
    let x = Math.imul(t ^ (t >>> 15), t | 1)
    x ^= x + Math.imul(x ^ (x >>> 7), x | 61)
    return ((x ^ (x >>> 14)) >>> 0) / 4294967296
  }
}
const rand = mulberry32(20260612)

type Tower = {
  x: number
  w: number
  h: number
  delay: number
  glass?: boolean // برج زجاجي (انعكاس بدل النوافذ المنفردة)
  spire?: number
  antenna?: number
  setback?: [number, number] // [عرض، ارتفاع] طابق متراجع
}

// الطبقة البعيدة — ظلال باهتة خلف الضباب
const FAR: Tower[] = [
  { x: -10, w: 90, h: 200, delay: 0.1 },
  { x: 95, w: 70, h: 265, delay: 0.25 },
  { x: 200, w: 105, h: 185, delay: 0.18 },
  { x: 340, w: 75, h: 290, delay: 0.3 },
  { x: 450, w: 95, h: 215, delay: 0.15 },
  { x: 590, w: 80, h: 320, delay: 0.28 },
  { x: 700, w: 110, h: 195, delay: 0.2 },
  { x: 845, w: 78, h: 280, delay: 0.32 },
  { x: 950, w: 100, h: 210, delay: 0.22 },
  { x: 1080, w: 85, h: 255, delay: 0.27 },
]

// الطبقة الوسطى
const MID: Tower[] = [
  { x: 40, w: 110, h: 295, delay: 0.45 },
  { x: 215, w: 88, h: 380, delay: 0.6 },
  { x: 415, w: 120, h: 310, delay: 0.5 },
  { x: 640, w: 95, h: 400, delay: 0.65 },
  { x: 820, w: 125, h: 330, delay: 0.55 },
  { x: 1010, w: 90, h: 365, delay: 0.7 },
]

// الطبقة القريبة — أبراج مفصّلة بنوافذ فردية
const NEAR: Tower[] = [
  { x: 60, w: 132, h: 340, delay: 0.85, setback: [92, 48], antenna: 55 },
  { x: 250, w: 150, h: 445, delay: 1.1, glass: true, spire: 0, antenna: 70 },
  { x: 465, w: 175, h: 530, delay: 1.3, spire: 95, antenna: 70 },
  { x: 700, w: 145, h: 405, delay: 1.0, setback: [102, 52] },
  { x: 902, w: 165, h: 315, delay: 1.45, glass: true, setback: [118, 44] },
]
const RISE_S = 1.35

// ───── توليد النوافذ المضاءة مسبقاً (خارج الـrender) ─────
type Win = { x: number; y: number; w: number; h: number; fill: string; delay: number; op: number }
const WARM = ['#ffd9a0', '#ffe9c4', '#f7c87e', '#ffe2ae']
const COOL = ['#cfe2ff', '#e3eeff', '#bcd4f5']

function buildWindows(t: Tower): Win[] {
  const wins: Win[] = []
  const cell = { w: 17, h: 21 }
  const cols = Math.floor((t.w - 18) / cell.w)
  const rows = Math.floor((t.h - 34) / cell.h)
  const ox = t.x + (t.w - cols * cell.w) / 2 + 3
  const oy = GROUND - t.h + 18
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const p = rand()
      if (p > 0.52) continue // نوافذ مطفأة — واقعية
      const warm = rand() < 0.72
      wins.push({
        x: ox + c * cell.w,
        y: oy + r * cell.h,
        w: cell.w - 7,
        h: cell.h - 9,
        fill: warm
          ? WARM[Math.floor(rand() * WARM.length)]
          : COOL[Math.floor(rand() * COOL.length)],
        delay: 2.0 + rand() * 1.5,
        op: 0.55 + rand() * 0.45,
      })
    }
  }
  return wins
}
const NEAR_WINDOWS: Win[][] = NEAR.map((t) => (t.glass ? [] : buildWindows(t)))

// نوافذ متفرقة للطبقة الوسطى (كثافة منخفضة)
const MID_WINDOWS: Win[][] = MID.map((t) => {
  const all = buildWindows(t)
  return all.filter(() => rand() < 0.4).map((w) => ({ ...w, op: w.op * 0.5 }))
})

// طيور عابرة
const BIRDS = [0, 1, 2].map((i) => ({
  y: 150 + rand() * 110,
  delay: 2.2 + i * 0.5 + rand() * 0.4,
  dur: 6 + rand() * 3,
  scale: 0.7 + rand() * 0.6,
}))

/**
 * loop=true: تُعاد الافتتاحية بلا نهاية ولا تُخفى ولا تُتخطّى —
 * وضع صفحة الهبوط (النطاق التعريفي)، حيث الأنيميشن نفسه هو المحتوى.
 */
export default function IntroSplash({ loop = false }: { loop?: boolean }) {
  const reduced =
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches

  // بوضع التكرار لا نُخفي المشهد إطلاقاً (الصفحة ستبقى فارغة لولاه)
  const [phase, setPhase] = useState<'play' | 'leave' | 'done'>(
    reduced && !loop ? 'done' : 'play',
  )
  const [run, setRun] = useState(0)
  const timers = useRef<number[]>([])

  const dismiss = useCallback(() => {
    if (loop) return // صفحة الهبوط: لا تخطّي
    setPhase((p) => (p === 'play' ? 'leave' : p))
  }, [loop])

  useEffect(() => {
    if (phase === 'play') {
      // بالتكرار: أعد تشغيل المشهد من بدايته بدل إخفائه
      timers.current.push(
        window.setTimeout(
          loop ? () => setRun((r) => r + 1) : dismiss,
          loop ? TOTAL_MS : TOTAL_MS - LEAVE_MS,
        ),
      )
    } else if (phase === 'leave') {
      timers.current.push(window.setTimeout(() => setPhase('done'), LEAVE_MS))
    }
    return () => {
      timers.current.forEach(clearTimeout)
      timers.current = []
    }
  }, [phase, dismiss, loop, run])

  if (phase === 'done') return null

  const riseStyle = (t: Tower, extra = 0) =>
    ({
      animationDelay: `${t.delay}s`,
      ['--rise' as string]: `${t.h + (t.spire ?? 0) + (t.antenna ?? 0) + (t.setback?.[1] ?? 0) + extra + 50}px`,
    }) as React.CSSProperties

  // برج بعيد/متوسط — كتلة صامتة بمنظور جوي (مع نوافذ اختيارية ترتفع معه)
  const silhouette = (t: Tower, i: number, cls: string, fill: string, wins?: Win[]) => (
    <g key={i} className={`twr ${cls}`} style={riseStyle(t)}>
      <rect x={t.x} y={GROUND - t.h} width={t.w} height={t.h} fill={fill} />
      <rect x={t.x + 5} y={GROUND - t.h - 5} width={t.w - 10} height={5} fill={fill} />
      {wins?.map((w, wi) => (
        <rect
          key={wi}
          className="win"
          style={
            {
              animationDelay: `${w.delay + 0.3}s`,
              ['--wop' as string]: w.op,
            } as React.CSSProperties
          }
          x={w.x}
          y={w.y}
          width={w.w}
          height={w.h}
          fill={w.fill}
        />
      ))}
    </g>
  )

  // برج قريب مفصّل
  const nearTower = (t: Tower, i: number) => {
    const bodyY = GROUND - t.h
    let topY = bodyY
    const sb = t.setback
      ? {
          x: t.x + (t.w - t.setback[0]) / 2,
          y: bodyY - t.setback[1],
          w: t.setback[0],
          h: t.setback[1],
        }
      : null
    if (sb) topY = sb.y
    const spireTip = topY - (t.spire ?? 0)
    const bodyFill = t.glass ? 'url(#glassBody)' : 'url(#nearBody)'

    return (
      <g key={i} className="twr twr-near" style={riseStyle(t)}>
        {/* الجسم */}
        <rect x={t.x} y={bodyY} width={t.w} height={t.h} fill={bodyFill} />
        {/* نسيج نوافذ خافت على كامل الواجهة */}
        {!t.glass && (
          <rect
            x={t.x + 6}
            y={bodyY + 12}
            width={t.w - 12}
            height={t.h - 20}
            fill="url(#facadeTex)"
            opacity={0.5}
          />
        )}
        {/* أعمدة واجهة زجاجية + لمعة انعكاس قطرية */}
        {t.glass && (
          <>
            <rect x={t.x} y={bodyY} width={t.w} height={t.h} fill="url(#mullions)" />
            <rect
              x={t.x}
              y={bodyY}
              width={t.w}
              height={t.h}
              fill="url(#glassSheen)"
              className="sheen"
            />
            <rect x={t.x} y={bodyY} width={t.w} height={t.h} fill="url(#skyRefl)" opacity={0.5} />
          </>
        )}
        {/* إضاءة حافة فجرية على الجانب المواجه للشمس */}
        <rect x={t.x + t.w - 4} y={bodyY} width={4} height={t.h} fill="url(#rimLight)" />
        {/* الطابق المتراجع */}
        {sb && (
          <>
            <rect x={sb.x} y={sb.y} width={sb.w} height={sb.h} fill={bodyFill} />
            <rect x={sb.x + sb.w - 3} y={sb.y} width={3} height={sb.h} fill="url(#rimLight)" />
          </>
        )}
        {/* القمة المدبّبة */}
        {t.spire ? (
          <path
            d={`M ${t.x + t.w / 2 - 17} ${topY} L ${t.x + t.w / 2} ${spireTip} L ${t.x + t.w / 2 + 17} ${topY} Z`}
            fill="url(#nearBody)"
          />
        ) : null}
        {/* الهوائي والمنارة */}
        {t.antenna ? (
          <g>
            <line
              x1={t.x + t.w / 2}
              y1={spireTip}
              x2={t.x + t.w / 2}
              y2={spireTip - t.antenna}
              stroke="#0e1626"
              strokeWidth={2.5}
            />
            <circle
              className="beacon"
              style={{ animationDelay: `${2.2 + i * 0.55}s` }}
              cx={t.x + t.w / 2}
              cy={spireTip - t.antenna}
              r={3}
            />
            <circle
              className="beacon beacon-halo"
              style={{ animationDelay: `${2.2 + i * 0.55}s` }}
              cx={t.x + t.w / 2}
              cy={spireTip - t.antenna}
              r={9}
            />
          </g>
        ) : null}
        {/* النوافذ المضاءة — فردية وعشوائية */}
        {NEAR_WINDOWS[i].map((w, wi) => (
          <rect
            key={wi}
            className="win"
            style={
              { animationDelay: `${w.delay}s`, ['--wop' as string]: w.op } as React.CSSProperties
            }
            x={w.x}
            y={w.y}
            width={w.w}
            height={w.h}
            rx={0.8}
            fill={w.fill}
          />
        ))}
      </g>
    )
  }

  return (
    <div
      key={run}
      className={`aufq-intro ${phase === 'leave' ? 'aufq-intro-leave' : ''}`}
      onClick={dismiss}
      role="presentation"
    >
      <style>{INTRO_CSS}</style>

      <svg
        className="aufq-intro-svg"
        viewBox="0 0 1200 700"
        preserveAspectRatio="xMidYMax slice"
        aria-hidden="true"
      >
        <defs>
          {/* سماء فجر فوتوغرافية */}
          <linearGradient id="sky" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#0a1122" />
            <stop offset="38%" stopColor="#16243f" />
            <stop offset="62%" stopColor="#2c4266" />
            <stop offset="78%" stopColor="#5b6483" />
            <stop offset="88%" stopColor="#a97f5e" />
            <stop offset="96%" stopColor="#e7ae66" />
            <stop offset="100%" stopColor="#f6cd8a" />
          </linearGradient>
          {/* توهّج الشمس */}
          <radialGradient id="sunGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#ffedc0" stopOpacity="0.95" />
            <stop offset="22%" stopColor="#ffd98e" stopOpacity="0.55" />
            <stop offset="55%" stopColor="#eaa75f" stopOpacity="0.22" />
            <stop offset="100%" stopColor="#eaa75f" stopOpacity="0" />
          </radialGradient>
          {/* أجسام الأبراج القريبة — تدرّج بارد داكن مع قاعدة أدفأ (ضوء الفجر) */}
          <linearGradient id="nearBody" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#2a3854" />
            <stop offset="70%" stopColor="#1b2740" />
            <stop offset="100%" stopColor="#23293e" />
          </linearGradient>
          <linearGradient id="glassBody" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#33455f" />
            <stop offset="55%" stopColor="#22334d" />
            <stop offset="100%" stopColor="#2b3146" />
          </linearGradient>
          {/* انعكاس السماء على الزجاج */}
          <linearGradient id="skyRefl" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#7d92b5" stopOpacity="0.35" />
            <stop offset="45%" stopColor="#54637f" stopOpacity="0.12" />
            <stop offset="85%" stopColor="#d99e5d" stopOpacity="0.22" />
            <stop offset="100%" stopColor="#f0bd77" stopOpacity="0.3" />
          </linearGradient>
          {/* لمعة قطرية زجاجية */}
          <linearGradient id="glassSheen" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#ffffff" stopOpacity="0" />
            <stop offset="38%" stopColor="#ffffff" stopOpacity="0.13" />
            <stop offset="50%" stopColor="#ffffff" stopOpacity="0.02" />
            <stop offset="66%" stopColor="#ffffff" stopOpacity="0.09" />
            <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
          </linearGradient>
          {/* إضاءة حافة دافئة من جهة الشمس */}
          <linearGradient id="rimLight" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#f3c27c" stopOpacity="0.0" />
            <stop offset="55%" stopColor="#f3c27c" stopOpacity="0.25" />
            <stop offset="100%" stopColor="#ffd795" stopOpacity="0.55" />
          </linearGradient>
          {/* نسيج نوافذ مطفأة (ملمس واجهة) */}
          <pattern id="facadeTex" width="17" height="21" patternUnits="userSpaceOnUse">
            <rect x="3" y="4" width="10" height="12" rx="1" fill="#39496b" opacity="0.5" />
          </pattern>
          {/* أعمدة الواجهة الزجاجية */}
          <pattern id="mullions" width="14" height="700" patternUnits="userSpaceOnUse">
            <rect x="13" width="1" height="700" fill="#0d1526" opacity="0.5" />
          </pattern>
          {/* ضباب عمق */}
          <filter id="blurFar" x="-10%" y="-10%" width="120%" height="120%">
            <feGaussianBlur stdDeviation="3.2" />
          </filter>
          <filter id="blurMid" x="-10%" y="-10%" width="120%" height="120%">
            <feGaussianBlur stdDeviation="1.1" />
          </filter>
          <filter id="soft" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="7" />
          </filter>
          {/* حبيبات فيلم */}
          <filter id="grain">
            <feTurbulence
              type="fractalNoise"
              baseFrequency="0.9"
              numOctaves="2"
              stitchTiles="stitch"
            />
            <feColorMatrix
              type="matrix"
              values="0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0.35 0.35 0.35 0 0"
            />
          </filter>
          {/* تظليل عدسة */}
          <radialGradient id="vignette" cx="50%" cy="46%" r="72%">
            <stop offset="0%" stopColor="#000000" stopOpacity="0" />
            <stop offset="74%" stopColor="#000000" stopOpacity="0" />
            <stop offset="100%" stopColor="#000000" stopOpacity="0.5" />
          </radialGradient>
          <clipPath id="aboveGround">
            <rect x="0" y="0" width="1200" height={GROUND} />
          </clipPath>
        </defs>

        {/* ═ الكاميرا: pull-back بطيء ═ */}
        <g className="cam">
          {/* السماء */}
          <rect width="1200" height="700" fill="url(#sky)" />

          {/* نجوم خافتة تذوب مع الفجر */}
          <g fill="#dfe8ff">
            {[
              [110, 55, 1.3],
              [300, 105, 0.9],
              [520, 45, 1.4],
              [705, 88, 1.0],
              [880, 38, 1.2],
              [1030, 95, 0.9],
              [1140, 55, 1.3],
              [420, 150, 0.8],
              [205, 185, 0.7],
            ].map(([cx, cy, r], i) => (
              <circle
                key={i}
                className="star"
                style={{ animationDelay: `${i * 0.25}s` }}
                cx={cx}
                cy={cy}
                r={r}
              />
            ))}
          </g>

          {/* الشمس + التوهّج خلف الأفق */}
          <g className="dawn">
            <circle cx="765" cy="600" r="300" fill="url(#sunGlow)" />
            <circle cx="765" cy="608" r="30" fill="#ffe9bd" filter="url(#soft)" />
            {/* أشعة حجمية */}
            <g className="rays" fill="#ffdf9e">
              <polygon points="765,608 520,0 660,0" opacity="0.05" filter="url(#soft)" />
              <polygon points="765,608 760,0 905,0" opacity="0.07" filter="url(#soft)" />
              <polygon points="765,608 1010,0 1130,0" opacity="0.045" filter="url(#soft)" />
            </g>
          </g>

          {/* سحب فجرية مضاءة من الأسفل */}
          <g className="clouds">
            <g className="cloud cloud-a">
              <ellipse
                cx="250"
                cy="130"
                rx="170"
                ry="17"
                fill="#1d2a45"
                opacity="0.55"
                filter="url(#soft)"
              />
              <ellipse
                cx="265"
                cy="140"
                rx="150"
                ry="6"
                fill="#caa06b"
                opacity="0.30"
                filter="url(#soft)"
              />
            </g>
            <g className="cloud cloud-b">
              <ellipse
                cx="880"
                cy="185"
                rx="210"
                ry="15"
                fill="#1c2944"
                opacity="0.5"
                filter="url(#soft)"
              />
              <ellipse
                cx="895"
                cy="194"
                rx="180"
                ry="5"
                fill="#d8ab6e"
                opacity="0.33"
                filter="url(#soft)"
              />
            </g>
            <g className="cloud cloud-c">
              <ellipse
                cx="540"
                cy="80"
                rx="140"
                ry="12"
                fill="#16213a"
                opacity="0.45"
                filter="url(#soft)"
              />
            </g>
          </g>

          {/* ═ المدينة (مقصوصة عند الأرض — الأبراج تخرج من تحتها) ═ */}
          {/* الطبقة البعيدة: خلف الضباب */}
          <g clipPath="url(#aboveGround)" filter="url(#blurFar)" opacity="0.5">
            {FAR.map((t, i) => silhouette(t, i, 'twr-far', '#7e90ad'))}
          </g>

          {/* الطبقة الوسطى */}
          <g clipPath="url(#aboveGround)" filter="url(#blurMid)" opacity="0.82">
            {MID.map((t, i) => silhouette(t, i, 'twr-mid', '#43577c', MID_WINDOWS[i]))}
          </g>

          {/* ضباب الأفق بين الطبقات */}
          <rect
            className="haze"
            x="0"
            y={GROUND - 130}
            width="1200"
            height="130"
            fill="#e3a96a"
            opacity="0.13"
            filter="url(#soft)"
          />

          {/* الطبقة القريبة المفصّلة */}
          <g clipPath="url(#aboveGround)">
            {NEAR.map((t, i) => nearTower(t, i))}

            {/* غبار البناء عند الارتطام */}
            {NEAR.map((t, i) => (
              <ellipse
                key={`d${i}`}
                className="dust"
                style={{ animationDelay: `${t.delay + RISE_S * 0.7}s` }}
                cx={t.x + t.w / 2}
                cy={GROUND - 4}
                rx={t.w * 0.95}
                ry={15}
                fill="#c8b187"
                filter="url(#soft)"
              />
            ))}
          </g>

          {/* الأرض الرطبة + الانعكاس */}
          <rect x="0" y={GROUND} width="1200" height="60" fill="#070d1a" />
          <rect x="0" y={GROUND} width="1200" height="2" fill="#f0bd77" opacity="0.3" />
          <g
            transform={`translate(0 ${GROUND * 2}) scale(1 -1)`}
            opacity="0.16"
            filter="url(#blurMid)"
          >
            <g clipPath="url(#aboveGround)">
              {NEAR.map((t, i) => (
                <rect
                  key={i}
                  className="twr twr-near"
                  style={riseStyle(t)}
                  x={t.x}
                  y={GROUND - t.h * 0.35}
                  width={t.w}
                  height={t.h * 0.35}
                  fill="#4d648c"
                />
              ))}
            </g>
          </g>
          {/* لمعة الشمس على الأرض الرطبة */}
          <rect
            className="dawn"
            x="700"
            y={GROUND}
            width="130"
            height="58"
            fill="#e7ae66"
            opacity="0.10"
            filter="url(#soft)"
          />

          {/* طيور عابرة */}
          <g className="birds" stroke="#0c1322" strokeWidth="2.2" fill="none" strokeLinecap="round">
            {BIRDS.map((b, i) => (
              <g
                key={i}
                className="bird"
                style={
                  {
                    animationDelay: `${b.delay}s`,
                    animationDuration: `${b.dur}s`,
                    top: 0,
                  } as React.CSSProperties
                }
              >
                <path
                  transform={`translate(0 ${b.y}) scale(${b.scale})`}
                  d="M 0 0 Q 7 -7 14 0 Q 21 -7 28 0"
                />
              </g>
            ))}
          </g>

          {/* ضباب أرضي أمامي خفيف */}
          <rect
            className="haze haze-front"
            x="0"
            y={GROUND - 55}
            width="1200"
            height="70"
            fill="#bfa478"
            opacity="0.08"
            filter="url(#soft)"
          />
        </g>

        {/* ═ طبقات ما بعد المعالجة (خارج حركة الكاميرا) ═ */}
        <rect width="1200" height="700" filter="url(#grain)" opacity="0.05" className="grain" />
        <rect width="1200" height="700" fill="url(#vignette)" />
      </svg>

      {/* الشعار — يترسم بحركة ثلاثية الأبعاد بعد اكتمال نهوض الأبراج */}
      <div className="uqi-scene">
        <div className="uqi-stage">
          {/* خلفية ضبابية خلف الشعار حتى يبرز فوق المدينة */}
          <div className="uqi-backdrop" />
          <svg
            className="uqi-lockup"
            viewBox="0 0 440 210"
            role="img"
            aria-label="أفق UFUQ"
            style={{ direction: 'ltr' }}
          >
            <defs>
              {/* قناع بأشكال الشعار — حتى تمر اللمعة فوق الأحرف فقط */}
              <mask id="uqiLogoMask">
                <rect x="0" y="0" width="440" height="210" fill="black" />
                <path
                  d="M 63 52 L 63 132 A 37 37 0 0 0 137 132 L 137 128"
                  fill="none"
                  stroke="white"
                  strokeWidth="38"
                  strokeLinecap="butt"
                />
                {STRIPE_YS.map((y) => (
                  <rect key={y} x="118" y={y} width="38" height="3.8" rx="1.4" fill="white" />
                ))}
                <circle cx="137" cy="30" r="15" fill="white" />
                <text
                  x="205"
                  y="100"
                  fill="white"
                  fontSize="52"
                  fontWeight="600"
                  fontFamily="'AlexandriaBrand','Geeza Pro',serif"
                >
                  أفـــــق
                </text>
                <text
                  x="205"
                  y="172"
                  fill="white"
                  fontSize="52"
                  fontWeight="600"
                  letterSpacing="5"
                  fontFamily="'PoppinsBrand',system-ui,sans-serif"
                >
                  UFUQ
                </text>
              </mask>
              <linearGradient id="uqiSheenGrad" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0" stopColor="#D1AB29" stopOpacity="0" />
                <stop offset="0.4" stopColor="#D1AB29" stopOpacity="0.35" />
                <stop offset="0.5" stopColor="#FFFFFF" stopOpacity="0.55" />
                <stop offset="0.6" stopColor="#D1AB29" stopOpacity="0.35" />
                <stop offset="1" stopColor="#D1AB29" stopOpacity="0" />
              </linearGradient>
            </defs>

            {/* ١) الجزء الصلب — كريمي على سماء الفجر: من أعلى اليسار نزولاً ثم القوس نحو اليمين */}
            <path
              className="uqi-sweep"
              d="M 63 52 L 63 132 A 37 37 0 0 0 137 132 L 137 128"
              fill="none"
              stroke="#F7F5EF"
              strokeWidth="38"
              strokeLinecap="butt"
            />
            {/* ٢) ١٤ خطاً ذهبياً تُكمل الساق اليمنى */}
            <g fill="#D1AB29">
              {STRIPE_YS.map((y, i) => (
                <rect
                  key={y}
                  className={`uqi-stripe uqi-s${i + 1}`}
                  x="118"
                  y={y}
                  width="38"
                  height="3.8"
                  rx="1.4"
                />
              ))}
            </g>
            {/* ٣) الكرة الذهبية */}
            <circle className="uqi-dot" cx="137" cy="30" r="15" fill="#D1AB29" />
            {/* ٤) الاسم يظهر جانبياً */}
            <text
              className="uqi-brand uqi-brand-ar"
              x="205"
              y="100"
              fill="#D1AB29"
              fontSize="52"
              fontWeight="600"
              fontFamily="'AlexandriaBrand','Geeza Pro',serif"
            >
              أفـــــق
            </text>
            <text
              className="uqi-brand uqi-brand-en"
              x="205"
              y="172"
              fill="#F7F5EF"
              fontSize="52"
              fontWeight="600"
              letterSpacing="5"
              fontFamily="'PoppinsBrand',system-ui,sans-serif"
            >
              UFUQ
            </text>

            {/* ٥) اللمعة الذهبية تعبر الأحرف بعد الاكتمال */}
            <g mask="url(#uqiLogoMask)">
              <g transform="skewX(-18)">
                <rect
                  className="uqi-sheenbar"
                  x="-140"
                  y="-40"
                  width="130"
                  height="290"
                  fill="url(#uqiSheenGrad)"
                />
              </g>
            </g>
          </svg>
        </div>
      </div>

      {!loop && (
        <button type="button" className="aufq-intro-skip" onClick={dismiss}>
          تخطي ←
        </button>
      )}
    </div>
  )
}

const INTRO_CSS = `
.aufq-intro {
  position: fixed; inset: 0; z-index: 9999;
  background: #0a1122; overflow: hidden; cursor: pointer;
  animation: aufqIntroIn 0.6s ease both;
}
.aufq-intro-leave { animation: aufqIntroOut ${LEAVE_MS}ms ease forwards; pointer-events: none; }
@keyframes aufqIntroIn { from { opacity: 0; } to { opacity: 1; } }
@keyframes aufqIntroOut { to { opacity: 0; } }

.aufq-intro-svg { position: absolute; inset: 0; width: 100%; height: 100%; }

/* حركة كاميرا سينمائية بطيئة */
.cam { transform-origin: 600px 660px; animation: camPull 5.2s cubic-bezier(0.25, 0.6, 0.35, 1) both; }
@keyframes camPull { from { transform: scale(1.075) translateY(8px); } to { transform: scale(1) translateY(0); } }

/* صعود الأبراج من تحت الأرض */
.twr {
  transform: translateY(var(--rise));
  animation: twrRise ${RISE_S}s cubic-bezier(0.18, 0.8, 0.26, 1.02) both;
  will-change: transform;
}
.twr-far { animation-duration: 0.9s; animation-timing-function: cubic-bezier(0.2, 0.8, 0.3, 1); }
.twr-mid { animation-duration: 1.1s; animation-timing-function: cubic-bezier(0.2, 0.8, 0.3, 1); }
@keyframes twrRise { to { transform: translateY(0); } }

/* النوافذ: تترسّخ بوميض تشغيل واقعي ثم تستقر على شدّتها العشوائية */
.win { opacity: 0; animation: winOn 0.5s steps(2, end) both; }
g .win.twr-mid { animation-name: winOn, twrRise; animation-duration: 0.5s, 1.1s; animation-fill-mode: both, both; }
@keyframes winOn {
  0% { opacity: 0; }
  40% { opacity: calc(var(--wop) * 0.4); }
  60% { opacity: calc(var(--wop) * 0.1); }
  100% { opacity: var(--wop); }
}

/* منارات حمراء */
.beacon { opacity: 0; fill: #ff6a55; animation: beaconBlink 2.2s ease-in-out infinite; }
.beacon-halo { fill: #ff6a55; filter: blur(4px); animation: beaconHalo 2.2s ease-in-out infinite; }
@keyframes beaconBlink { 0%, 100% { opacity: 0.05; } 50% { opacity: 0.95; } }
@keyframes beaconHalo { 0%, 100% { opacity: 0.02; } 50% { opacity: 0.30; } }

/* الفجر يتنامى */
.dawn { opacity: 0.25; animation: dawnUp 3.2s ease 0.3s both; }
@keyframes dawnUp { to { opacity: 1; } }
.rays { animation: raysPulse 4.5s ease-in-out 1.2s infinite alternate; }
@keyframes raysPulse { from { opacity: 0.75; } to { opacity: 1; } }

/* النجوم تذوب */
.star { opacity: 0; animation: starFade 3s ease both; }
@keyframes starFade { 0% { opacity: 0; } 22% { opacity: 0.8; } 60% { opacity: 0.35; } 100% { opacity: 0.03; } }

/* السحب تنساب */
.cloud-a { animation: drift 22s linear infinite alternate; }
.cloud-b { animation: drift 28s linear infinite alternate-reverse; }
.cloud-c { animation: drift 32s linear infinite alternate; }
@keyframes drift { from { transform: translateX(-45px); } to { transform: translateX(55px); } }

/* الضباب يتنفس */
.haze { animation: hazeBreath 6s ease-in-out infinite alternate; }
@keyframes hazeBreath { from { opacity: 0.08; } to { opacity: 0.16; } }
.haze-front { animation-delay: 1.6s; }

/* غبار الارتطام */
.dust { opacity: 0; transform-box: fill-box; transform-origin: center bottom; animation: dustPuff 1.1s ease-out both; }
@keyframes dustPuff {
  0% { opacity: 0; transform: scale(0.25, 0.35); }
  22% { opacity: 0.5; transform: scale(0.85, 0.95); }
  100% { opacity: 0; transform: scale(1.7, 1.45); }
}

/* لمعة الزجاج تنزلق */
.sheen { animation: sheenSlide 3.6s ease-in-out 1.6s both; }
@keyframes sheenSlide { from { transform: translateX(-30px); opacity: 0.4; } to { transform: translateX(25px); opacity: 1; } }

/* الطيور */
.bird { opacity: 0; transform: translateX(-60px); animation-name: birdFly; animation-timing-function: linear; animation-fill-mode: both; }
@keyframes birdFly {
  0% { opacity: 0; transform: translateX(-60px) translateY(0); }
  8% { opacity: 0.8; }
  50% { transform: translateX(620px) translateY(-22px); }
  92% { opacity: 0.8; }
  100% { opacity: 0; transform: translateX(1280px) translateY(-8px); }
}

/* حبيبات الفيلم تهتز */
.grain { animation: grainShift 0.45s steps(2) infinite; }
@keyframes grainShift { 0% { transform: translate(0, 0); } 50% { transform: translate(-1.5%, 1%); } 100% { transform: translate(1%, -1.5%); } }

/* ═ الشعار ثلاثي الأبعاد — يترسم فوق المدينة بعد نهوض الأبراج ═ */
.uqi-scene {
  position: absolute; top: 8%; left: 50%; transform: translateX(-50%);
  width: min(74vw, 440px); pointer-events: none; z-index: 2;
  perspective: 1100px; perspective-origin: 50% 40%;
}
/* يولد مائلاً بالعمق ويستوي مع اكتمال رسمه، ثم يطفو بثلاثية الأبعاد */
.uqi-stage {
  transform-style: preserve-3d; will-change: transform;
  animation:
    uqi3d-intro 5.2s cubic-bezier(0.3, 0.8, 0.3, 1) ${LOGO_START_S}s both,
    uqi3d-float 6s ease-in-out ${LOGO_START_S + 5.2}s infinite alternate;
}
@keyframes uqi3d-intro {
  0%   { transform: rotateX(42deg) rotateY(-28deg) translateZ(-120px) translateY(26px); }
  45%  { transform: rotateX(20deg) rotateY(-13deg) translateZ(-40px) translateY(10px); }
  100% { transform: rotateX(7deg)  rotateY(-4deg)  translateZ(0)      translateY(0); }
}
@keyframes uqi3d-float {
  from { transform: rotateX(7deg) rotateY(-4deg) translateY(0)    translateZ(0); }
  to   { transform: rotateX(2deg) rotateY(6deg)  translateY(-9px) translateZ(18px); }
}
/* خلفية ضبابية داكنة خلف الشعار — تميل وتطفو معه */
.uqi-backdrop {
  position: absolute; inset: -9% -6%;
  border-radius: 28px;
  background: rgba(9, 16, 32, 0.44);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(247, 245, 239, 0.09);
  box-shadow: 0 20px 55px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(247, 245, 239, 0.06);
  opacity: 0;
  animation: uqi-backdrop-in 0.9s ease ${LOGO_START_S}s forwards;
}
@keyframes uqi-backdrop-in { to { opacity: 1; } }
.uqi-lockup {
  position: relative;
  display: block; width: 100%; height: auto;
  filter: drop-shadow(0 0 30px rgba(209, 171, 41, 0.3)) drop-shadow(0 8px 26px rgba(0, 0, 0, 0.45));
}
/* رسم الشعار — نفس التوقيتات المعتمدة مُزاحة لبدء ما بعد الأبراج */
.uqi-sweep {
  stroke-dasharray: 240; stroke-dashoffset: 240;
  animation: uqi-draw-sweep 1.2s cubic-bezier(0.45, 0, 0.25, 1) ${LOGO_START_S + 0.15}s forwards;
}
@keyframes uqi-draw-sweep { to { stroke-dashoffset: 0; } }
.uqi-stripe {
  transform: scaleX(0); transform-origin: left center; transform-box: fill-box;
  animation: uqi-draw-stripe 0.3s cubic-bezier(0.55, 0, 0.3, 1) forwards;
}
${STRIPE_YS.map((_, i) => `.uqi-s${i + 1} { animation-delay: ${(LOGO_START_S + 1.45 + i * 0.14).toFixed(2)}s; }`).join('\n')}
@keyframes uqi-draw-stripe { to { transform: scaleX(1); } }
.uqi-dot {
  opacity: 0; transform: scale(0.3); transform-origin: center; transform-box: fill-box;
  animation: uqi-pop 0.38s cubic-bezier(0.34, 1.56, 0.64, 1) ${LOGO_START_S + 3.6}s forwards;
}
@keyframes uqi-pop { to { opacity: 1; transform: scale(1); } }
.uqi-brand {
  opacity: 0; transform: translateX(-22px);
  animation: uqi-slide-in 0.55s cubic-bezier(0.25, 0.9, 0.3, 1) forwards;
}
.uqi-brand-ar { animation-delay: ${LOGO_START_S + 4.15}s; }
.uqi-brand-en { animation-delay: ${LOGO_START_S + 4.32}s; }
@keyframes uqi-slide-in { to { opacity: 1; transform: translateX(0); } }
/* اللمعة الذهبية تعبر أحرف الشعار بعد اكتماله */
.uqi-sheenbar {
  animation: uqi-sheen-run 3.6s ease-in-out ${LOGO_START_S + 5.4}s infinite;
  transform: translateX(-40px);
}
@keyframes uqi-sheen-run {
  0%   { transform: translateX(-40px); }
  38%  { transform: translateX(640px); }
  100% { transform: translateX(640px); }
}

/* زر التخطي */
.aufq-intro-skip {
  position: absolute; bottom: 26px; inset-inline-start: 26px;
  background: rgba(255, 255, 255, 0.07); color: rgba(255, 255, 255, 0.7);
  border: 1px solid rgba(255, 255, 255, 0.16); border-radius: 999px;
  padding: 7px 18px; font-size: 13px; cursor: pointer;
  font-family: 'IBM Plex Sans Arabic', 'Segoe UI', sans-serif;
  backdrop-filter: blur(4px); transition: background 0.2s, color 0.2s;
  opacity: 0; animation: skipIn 0.6s ease 1.3s both;
}
.aufq-intro-skip:hover { background: rgba(255, 255, 255, 0.15); color: #fff; }
@keyframes skipIn { to { opacity: 1; } }

@media (prefers-reduced-motion: reduce) { .aufq-intro { display: none; } }
`
